import {
  Button,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Stack,
  Typography,
  FormHelperText,
} from '@mui/material';
import { useCallback, useState, useEffect, useMemo } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useParams, useHistory } from 'react-router-dom';

import LoadingButton from '@/@mantis/components/@extended/LoadingButton';
import DashboardHeader from '@/@mui/dashboard/DashboardHeader';
import MuiPageWrapper from '@/@mui/MuiPageWrapper';
import { useGetAllBuildings } from '@/api/endpoints/building/useGetAllBuildings';
import { useGetContact } from '@/api/endpoints/contact/useGetContact';
import {
  useUpdateContact,
  ApiRequest_Update_Contact,
  UpdateContactDetailDto,
} from '@/api/endpoints/contact/useUpdateContact';
import { useGetAllPropertyLocations } from '@/api/endpoints/propertyLocation/useGetAllPropertyLocations';
import { useGetAllUnitsInPropertyLocation } from '@/api/endpoints/unit/useGetAllUnitsInPropertyLocation';
import { ContactRole } from '@/api/enums/index';
import { ArrowLeftIcon } from '@/assets/icons/ArrowLeftIcon';
import { PlusIcon } from '@/assets/icons/PlusIcon';
import { Trash01Icon } from '@/assets/icons/Trash01Icon';
import { PMPRoutes } from '@/config/routes';
import { usePropertyManager } from '@/context/PropertyManagerProvider';
import { getResolvedRoutePath } from '@/utils/router';

import FormAutocomplete, { Option } from '@/components/form/FormAutocomplete';
import FormSelect from '@/components/form/FormSelect';
import FormTextField from '@/components/form/FormTextField';

// Temporary fallback in case ContactRole is undefined
const ContactRoleFallback = {
  TENANT: 'TENANT',
  LANDLORD: 'LANDLORD',
  PROPERTY_MANAGER: 'PROPERTY_MANAGER',
  OWNER_REPRESENTATIVE: 'OWNER_REPRESENTATIVE',
  CLEANING_SERVICE: 'CLEANING_SERVICE',
  HVAC_CONTRACTOR: 'HVAC_CONTRACTOR',
  ELECTRIC_PROVIDER: 'ELECTRIC_PROVIDER',
  WATER_PROVIDER: 'WATER_PROVIDER',
  WASTEWATER_SERVICE: 'WASTEWATER_SERVICE',
  PROPANE_SUPPLIER: 'PROPANE_SUPPLIER',
  TRASH_REMOVAL: 'TRASH_REMOVAL',
  TREE_SERVICE: 'TREE_SERVICE',
  PEST_CONTROL: 'PEST_CONTROL',
  POOL_SERVICE: 'POOL_SERVICE',
  LAWN_CARE: 'LAWN_CARE',
  LANDSCAPING_VENDOR: 'LANDSCAPING_VENDOR',
  SECURITY_PROVIDER: 'SECURITY_PROVIDER',
  SOUND_SYSTEM_VENDOR: 'SOUND_SYSTEM_VENDOR',
  WATER_COOLER_VENDOR: 'WATER_COOLER_VENDOR',
  TAX_AUTHORITY: 'TAX_AUTHORITY',
  INSURANCE_AGENT: 'INSURANCE_AGENT',
  LEGAL_REPRESENTATIVE: 'LEGAL_REPRESENTATIVE',
  OTHER: 'OTHER',
};

const SafeContactRole = ContactRole || ContactRoleFallback;

interface ContactEditPageParams {
  id: string;
}

interface ContactMetadata {
  additional_details?: string;
  [key: string]: unknown;
}

interface ContactFormData
  extends Omit<
    ApiRequest_Update_Contact,
    'details' | 'building_ids' | 'unit_ids' | 'location_ids'
  > {
  details: UpdateContactDetailDto[];
  building_ids: Option[];
  unit_ids: Option[];
  location_ids: Option[];
  additional_details?: string;
}

const contactRoleOptions = [
  { label: 'Tenant', value: SafeContactRole.TENANT },
  { label: 'Landlord', value: SafeContactRole.LANDLORD },
  { label: 'Property Manager', value: SafeContactRole.PROPERTY_MANAGER },
  { label: 'Owner Representative', value: SafeContactRole.OWNER_REPRESENTATIVE },

  { label: 'Cleaning Service', value: SafeContactRole.CLEANING_SERVICE },
  { label: 'HVAC Contractor', value: SafeContactRole.HVAC_CONTRACTOR },
  { label: 'Electric Provider', value: SafeContactRole.ELECTRIC_PROVIDER },
  { label: 'Water Provider', value: SafeContactRole.WATER_PROVIDER },
  { label: 'Wastewater Service', value: SafeContactRole.WASTEWATER_SERVICE },
  { label: 'Propane Supplier', value: SafeContactRole.PROPANE_SUPPLIER },
  { label: 'Trash Removal', value: SafeContactRole.TRASH_REMOVAL },
  { label: 'Tree Service', value: SafeContactRole.TREE_SERVICE },
  { label: 'Pest Control', value: SafeContactRole.PEST_CONTROL },
  { label: 'Pool Service', value: SafeContactRole.POOL_SERVICE },
  { label: 'Lawn Care', value: SafeContactRole.LAWN_CARE },
  { label: 'Landscaping Vendor', value: SafeContactRole.LANDSCAPING_VENDOR },
  { label: 'Security Provider', value: SafeContactRole.SECURITY_PROVIDER },
  { label: 'Sound System Vendor', value: SafeContactRole.SOUND_SYSTEM_VENDOR },
  { label: 'Water Cooler Vendor', value: SafeContactRole.WATER_COOLER_VENDOR },

  { label: 'Tax Authority', value: SafeContactRole.TAX_AUTHORITY },
  { label: 'Insurance Agent', value: SafeContactRole.INSURANCE_AGENT },
  { label: 'Legal Representative', value: SafeContactRole.LEGAL_REPRESENTATIVE },

  { label: 'Other', value: SafeContactRole.OTHER },
];

const contactDetailTypeOptions = [
  { label: 'Email', value: 'email' },
  { label: 'Phone', value: 'phone' },
];

const contactDetailLabelOptions = [
  { label: 'Primary', value: 'primary' },
  { label: 'Work', value: 'work' },
  { label: 'Mobile', value: 'mobile' },
  { label: 'Home', value: 'home' },
  { label: 'Other', value: 'other' },
];

const ContactEditPage: React.FC = () => {
  const { id } = useParams<ContactEditPageParams>();
  const { selectedPropertyLocation } = usePropertyManager();
  const history = useHistory();
  const [formError, setFormError] = useState<Error | null>(null);

  const contactId = parseInt(id, 10);

  // Fetch contact data
  const {
    data: contact,
    isLoading: isLoadingContact,
    error: contactError,
  } = useGetContact(contactId);

  // Fetch all property locations for the property manager
  const { data: propertyLocations } = useGetAllPropertyLocations();

  // Fetch buildings for the current location
  const { data: buildings, isLoading: isLoadingBuildings } = useGetAllBuildings(
    { location_id: selectedPropertyLocation?.id },
    !!selectedPropertyLocation?.id
  );

  // Fetch units for the current location
  const { data: units, isLoading: isLoadingUnits } = useGetAllUnitsInPropertyLocation(
    { propertyLocationId: selectedPropertyLocation?.id },
    !!selectedPropertyLocation?.id
  );

  // Create options for property locations
  const propertyLocationOptions: Option[] = useMemo(
    () =>
      propertyLocations?.map(location => ({
        label: location.name,
        value: location.id,
      })) || [],
    [propertyLocations]
  );

  // Create options for buildings and units
  const buildingOptions: Option[] = useMemo(
    () =>
      buildings?.map(building => ({
        label: building.name || building.address_text,
        value: building.id,
      })) || [],
    [buildings]
  );

  const unitOptions: Option[] = useMemo(
    () =>
      units?.map(unit => ({
        label: unit.name,
        value: unit.id,
      })) || [],
    [units]
  );

  const form = useForm<ContactFormData>({
    defaultValues: {
      name: '',
      role: SafeContactRole.TENANT,
      details: [],
      building_ids: [],
      unit_ids: [],
      location_ids: [],
      additional_details: '',
    },
  });

  const {
    control,
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  const {
    fields: detailFields,
    append: appendDetail,
    remove: removeDetail,
  } = useFieldArray({
    control,
    name: 'details',
  });

  // Watch form values for validation and conditional display
  const watchedRole = watch('role');

  // Check if role is tenant to show building/unit fields
  const isTenantRole = watchedRole === SafeContactRole.TENANT;

  // Update form when contact data is loaded
  useEffect(() => {
    if (contact && propertyLocationOptions.length > 0) {
      const metadata = contact.metadata as ContactMetadata | null;

      // Get selected property location options from contact associations
      const selectedLocationIds =
        contact.associations
          ?.filter(assoc => assoc.location_id)
          ?.map(assoc => assoc.location_id)
          .filter((locationId): locationId is number => locationId !== null) || [];

      const selectedLocationOptions = propertyLocationOptions.filter(option =>
        selectedLocationIds.includes(Number(option.value))
      );

      // Get selected building and unit options from contact associations
      const selectedBuildingIds =
        contact.associations
          ?.filter(assoc => assoc.building_id)
          ?.map(assoc => assoc.building_id)
          .filter((buildingId): buildingId is number => buildingId !== null) || [];

      const selectedUnitIds =
        contact.associations
          ?.filter(assoc => assoc.unit_id)
          ?.map(assoc => assoc.unit_id)
          .filter((unitId): unitId is number => unitId !== null) || [];

      const selectedBuildingOptions = buildingOptions.filter(option =>
        selectedBuildingIds.includes(Number(option.value))
      );

      const selectedUnitOptions = unitOptions.filter(option =>
        selectedUnitIds.includes(Number(option.value))
      );

      reset({
        name: contact.name,
        role: contact.role as ContactRole,
        user_id: contact.user_id,
        metadata: contact.metadata,
        additional_details: metadata?.additional_details || '',
        details:
          contact.details?.map(detail => ({
            id: detail.id,
            type: detail.type,
            value: detail.value,
            label: detail.label || '',
            is_primary: detail.is_primary,
          })) || [],
        building_ids: selectedBuildingOptions,
        unit_ids: selectedUnitOptions,
        location_ids: selectedLocationOptions,
      });
    }
  }, [contact, reset, buildingOptions, unitOptions, propertyLocationOptions]);

  const updateContactMutation = useUpdateContact(contactId, {
    onSuccess: () => {
      history.push(getResolvedRoutePath(PMPRoutes.contactView, { id }));
    },
    onError: error => {
      setFormError(error);
    },
  });

  const handleBack = () => {
    history.push(getResolvedRoutePath(PMPRoutes.contactView, { id }));
  };

  const handleCancel = () => {
    history.push(getResolvedRoutePath(PMPRoutes.contactView, { id }));
  };

  const onSubmit = useCallback(
    (data: ContactFormData) => {
      setFormError(null);

      // Validate at least one location is selected
      if (!data.location_ids || data.location_ids.length === 0) {
        setFormError(new Error('At least one property location must be selected'));
        return;
      }

      // Filter out empty contact details
      const filteredDetails = data.details.filter(detail => detail.value.trim() !== '');

      // Prepare metadata with additional details
      const existingMetadata = (contact?.metadata as ContactMetadata) || {};
      const metadata: ContactMetadata = {
        ...existingMetadata,
      };

      if (data.additional_details?.trim()) {
        metadata.additional_details = data.additional_details.trim();
      } else {
        delete metadata.additional_details;
      }

      // Convert Option arrays back to number arrays
      const locationIds = data.location_ids.map(option => Number(option.value));
      const buildingIds = data.building_ids.map(option => Number(option.value));
      const unitIds = data.unit_ids.map(option => Number(option.value));

      return updateContactMutation.mutateAsync({
        name: data.name,
        role: data.role,
        user_id: data.user_id,
        metadata,
        details: filteredDetails.length > 0 ? filteredDetails : undefined,
        location_ids: locationIds,
        building_ids: buildingIds.length > 0 ? buildingIds : undefined,
        unit_ids: unitIds.length > 0 ? unitIds : undefined,
      });
    },
    [updateContactMutation, contact?.metadata]
  );

  const handleAddDetail = () => {
    appendDetail({ type: 'email', value: '', is_primary: false });
  };

  if (isLoadingContact || isLoadingBuildings || isLoadingUnits) {
    return (
      <MuiPageWrapper>
        <Stack
          direction="column"
          spacing={2}
          sx={{ flex: '1 1 100%', alignItems: 'center', justifyContent: 'center' }}
        >
          <CircularProgress />
          <Typography variant="body2" color="text.secondary">
            Loading contact...
          </Typography>
        </Stack>
      </MuiPageWrapper>
    );
  }

  if (contactError || !contact) {
    return (
      <MuiPageWrapper>
        <Stack direction="column" spacing={2} sx={{ flex: '1 1 100%' }}>
          <Typography variant="body2" color="error">
            Failed to load contact. Please try again.
          </Typography>
          <Button variant="outlined" onClick={handleBack}>
            Back to Contact
          </Button>
        </Stack>
      </MuiPageWrapper>
    );
  }

  return (
    <MuiPageWrapper>
      <Stack direction="column" spacing={3} sx={{ flex: '1 1 100%' }}>
        <DashboardHeader title="Edit Contact">
          <Button
            color="secondary"
            variant="outlined"
            size="medium"
            startIcon={<ArrowLeftIcon />}
            onClick={handleBack}
          >
            Back to Contact
          </Button>
        </DashboardHeader>

        <Typography variant="body3">
          Edit contact information and details. Select one or more property locations to associate
          with this contact.
        </Typography>

        <Card>
          <CardContent sx={{ p: 3 }}>
            <Stack component="form" onSubmit={handleSubmit(onSubmit)} spacing={4}>
              {/* Property Location Association */}
              <Stack spacing={3}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Property Location Association
                </Typography>

                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Select the property locations this contact is associated with. At least one
                  location is required.
                </Typography>

                <FormAutocomplete
                  name="location_ids"
                  label="Property Locations"
                  options={propertyLocationOptions}
                  control={control}
                  multiple
                  filterSelectedOptions
                  placeholder="Select property locations"
                  size="small"
                  sx={{ maxWidth: 600 }}
                  required
                />
              </Stack>

              {/* Basic Information */}
              <Stack spacing={3}>
                <Typography variant="h6" sx={{ mb: 1 }}>
                  Basic Information
                </Typography>

                <FormTextField
                  label="Contact Name"
                  placeholder="Enter contact name"
                  required
                  control={control}
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  {...register('name', {
                    required: 'Contact name is required',
                  })}
                  size="small"
                  sx={{ maxWidth: 400 }}
                />

                <FormSelect
                  label="Role"
                  placeholder="Select contact role"
                  required
                  control={control}
                  options={contactRoleOptions}
                  error={!!errors.role}
                  helperText={errors.role?.message}
                  {...register('role', {
                    required: 'Contact role is required',
                  })}
                  size="small"
                  sx={{ maxWidth: 400 }}
                />

                <FormTextField
                  label="Additional Details"
                  placeholder="Enter any additional information about this contact"
                  control={control}
                  error={!!errors.additional_details}
                  helperText={errors.additional_details?.message}
                  {...register('additional_details')}
                  multiline
                  rows={3}
                  size="small"
                  sx={{ maxWidth: 600 }}
                />
              </Stack>

              {/* Location Association - Only for Tenants */}
              {isTenantRole && (buildingOptions.length > 0 || unitOptions.length > 0) && (
                <Stack spacing={3}>
                  <Typography variant="h6" sx={{ mb: 1 }}>
                    Location Association (Optional)
                  </Typography>

                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    Associate this tenant with specific buildings or units. This is optional for
                    tenant contacts.
                  </Typography>

                  {buildingOptions.length > 0 && (
                    <FormAutocomplete
                      name="building_ids"
                      label="Buildings (Optional)"
                      options={buildingOptions}
                      control={control}
                      multiple
                      filterSelectedOptions
                      placeholder="Select buildings"
                      size="small"
                      sx={{ maxWidth: 600 }}
                    />
                  )}

                  {unitOptions.length > 0 && (
                    <FormAutocomplete
                      name="unit_ids"
                      label="Units (Optional)"
                      options={unitOptions}
                      control={control}
                      multiple
                      filterSelectedOptions
                      placeholder="Select units"
                      size="small"
                      sx={{ maxWidth: 600 }}
                    />
                  )}
                </Stack>
              )}

              {/* Contact Details */}
              <Stack spacing={3}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Typography variant="h6">Contact Details</Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PlusIcon />}
                    onClick={handleAddDetail}
                  >
                    Add Detail
                  </Button>
                </Stack>

                {detailFields.map((field, index) => (
                  <Stack
                    key={field.id}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={2}
                    alignItems="flex-start"
                  >
                    <FormSelect
                      label="Type"
                      control={control}
                      options={contactDetailTypeOptions}
                      {...register(`details.${index}.type` as const)}
                      sx={{
                        minWidth: { xs: '100%', sm: 100 },
                        maxWidth: { xs: '100%', sm: 120 },
                      }}
                      size="small"
                    />

                    <FormTextField
                      label="Value"
                      placeholder={
                        watch(`details.${index}.type`) === 'email'
                          ? 'Enter email address'
                          : 'Enter phone number'
                      }
                      control={control}
                      error={!!errors.details?.[index]?.value}
                      helperText={errors.details?.[index]?.value?.message}
                      {...register(`details.${index}.value` as const, {
                        required: 'Value is required',
                        pattern:
                          watch(`details.${index}.type`) === 'email'
                            ? {
                              value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                              message: 'Invalid email address',
                            }
                            : undefined,
                      })}
                      sx={{
                        flex: 1,
                        minWidth: { xs: '100%', sm: 200 },
                      }}
                      size="small"
                    />

                    <FormSelect
                      label="Label"
                      control={control}
                      options={contactDetailLabelOptions}
                      {...register(`details.${index}.label` as const)}
                      sx={{
                        minWidth: { xs: '100%', sm: 120 },
                        maxWidth: { xs: '100%', sm: 150 },
                      }}
                      size="small"
                    />

                    <IconButton
                      onClick={() => removeDetail(index)}
                      disabled={detailFields.length <= 1}
                      color="error"
                      sx={{
                        alignSelf: { xs: 'flex-start', sm: 'center' },
                        mt: { xs: 0, sm: 2 },
                      }}
                    >
                      <Trash01Icon />
                    </IconButton>
                  </Stack>
                ))}

                {detailFields.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                    No contact details added. Click "Add Detail" to add email or phone information.
                  </Typography>
                )}
              </Stack>

              {/* Form Error */}
              {formError && <FormHelperText error>{formError.message}</FormHelperText>}

              {/* Form Actions */}
              <Stack direction="row" spacing={2} sx={{ pt: 2 }}>
                <LoadingButton
                  type="submit"
                  variant="contained"
                  color="primary"
                  loading={isSubmitting || updateContactMutation.isPending}
                  disabled={isSubmitting || updateContactMutation.isPending}
                >
                  Save Changes
                </LoadingButton>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={handleCancel}
                  disabled={isSubmitting || updateContactMutation.isPending}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </MuiPageWrapper>
  );
};

export default ContactEditPage;
